/**
 * Translation helpers for photo-bundle import result messages.
 * Extracted from result-phase.tsx to keep that file within the max-lines limit.
 */
import type { useTranslation } from 'react-i18next';
import type { BulkPhotoImportResultRow } from '@ooh/contracts';

export const PHOTO_IMPORT_MESSAGE_KEY_BY_CODE = {
  uploaded_successfully: 'result.messages.uploadedSuccessfully',
  no_media_unit_found: 'result.messages.noMediaUnitFound',
  nested_zip_directory_not_supported: 'result.messages.nestedZipDirectoryNotSupported',
  unrecognized_image_file: 'result.messages.unrecognizedImageFile',
  could_not_parse_supplier_number: 'result.messages.couldNotParseSupplierNumber',
  photo_limit_reached: 'result.messages.photoLimitReached',
  photo_already_present: 'result.messages.photoAlreadyPresent',
  upload_failed: 'result.messages.uploadFailed',
  association_failed: 'result.messages.associationFailed',
  uploaded_renditions_failed: 'result.messages.uploadedRenditionsFailed',
  photo_renditions_failed: 'result.messages.photoRenditionsFailed',
} as const satisfies Record<string, string>;

type TFunction = ReturnType<typeof useTranslation<'mediaImport'>>['t'];

// Backward-compat for rows stored before the worker started emitting `code` + `params`.
// These regexes intentionally mirror the legacy English worker messages.
export function translateLegacyPhotoResultMessage(message: string, t: TFunction): string {
  if (message === 'Uploaded successfully') {
    return t('result.messages.uploadedSuccessfully');
  }

  if (message === 'Photo already present, kept') {
    return t('result.messages.photoAlreadyPresent');
  }

  const noMediaUnitMatch = message.match(
    /^Skipped: no media unit found for supplier number '(.+)'$/,
  );
  if (noMediaUnitMatch !== null) {
    return t('result.messages.noMediaUnitFound', { supplierNumber: noMediaUnitMatch[1] });
  }

  if (message === 'Skipped: nested ZIP directory entries are not supported') {
    return t('result.messages.nestedZipDirectoryNotSupported');
  }

  const invalidImageMatch = message.match(/^Skipped: not a recognized image file \((.+)\)$/);
  if (invalidImageMatch !== null) {
    return t('result.messages.unrecognizedImageFile', { extension: invalidImageMatch[1] });
  }

  if (message === 'Skipped: could not parse supplier number from filename') {
    return t('result.messages.couldNotParseSupplierNumber');
  }

  const photoLimitMatch = message.match(
    /^Skipped: photo limit reached \(max (\d+) photos per unit\)$/,
  );
  if (photoLimitMatch !== null) {
    return t('result.messages.photoLimitReached', { limit: photoLimitMatch[1] });
  }

  return message;
}

export function translatePhotoResultMessage(row: BulkPhotoImportResultRow, t: TFunction): string {
  if (row.code !== undefined && row.code in PHOTO_IMPORT_MESSAGE_KEY_BY_CODE) {
    const key =
      PHOTO_IMPORT_MESSAGE_KEY_BY_CODE[row.code as keyof typeof PHOTO_IMPORT_MESSAGE_KEY_BY_CODE];
    const translated = t(key as never, (row.params ?? {}) as never);
    return typeof translated === 'string' ? translated : row.message;
  }

  return translateLegacyPhotoResultMessage(row.message, t);
}
